import {
  firstDrinkLikeAddOn,
  getProductIncludedItems,
  getProductIncludedSummary,
  majorityIncludedFlag,
  productHasExplicitAssistantBundle,
  productIncludedHasDrink,
  productIncludedHasFries,
  productIsBurgerish,
  productSuggestsExtraProteinAddon,
} from '@/lib/productComposition';
import {
  resolveNeutralUpsellFallbackLines,
  resolvePreferredUpsellSuggestions,
  resolveReadableProductFamily,
  resolveReadableProductPhrase,
} from '@/lib/assistantProductLanguage';

function listRemovable(product) {
  const r = product?.removable_ingredients;
  if (!Array.isArray(r) || r.length === 0) return null;
  return r.map((x) => (typeof x === 'string' ? x : x?.name)).filter(Boolean);
}

function listAddOns(product) {
  const a = product?.add_ons;
  if (!Array.isArray(a) || a.length === 0) return null;
  return a.map((x) => {
    const name = x?.name || '';
    const price = Number(x?.price) || 0;
    return price > 0 ? `${name} (+$${price.toLocaleString('es-CL')})` : name;
  }).filter(Boolean);
}

const WANTS_SOLO_OR_NO_SIDE_RE =
  /\b(sola|solo|sin\s+papas|sin\s+acompañ|sin\s+acompan|sin\s+combo|no\s+combo|hamburguesa\s+sola|burger\s+sola|viene\s+sola|va\s+sola)\b/;

function wantsSoloOrNoSide(queryNorm) {
  return WANTS_SOLO_OR_NO_SIDE_RE.test(queryNorm);
}

function isSideInclusionAsk(queryNorm) {
  return (
    /\b(papa|papas|frita|bebida|gaseosa|jugo|acompañ)\b/.test(queryNorm) &&
    /\b(lleva|inclu|viene|trae|tiene|combo)\b/.test(queryNorm)
  );
}

const PROTEIN_QUERY_RE = /\b(extra|doble|carne|pollo|bacon)\b/;

/**
 * @param {object} ctx
 * @param {object} ctx.product
 * @param {string} ctx.queryNorm
 * @param {object} ctx.menuData
 * @param {object} ctx.business
 * @param {'default'|'burger'} variant
 */
function buildDescribeCompositionLines(ctx, variant) {
  const { product, queryNorm, business, menuData } = ctx;
  const phrase = resolveReadableProductPhrase(product, business, menuData);
  const name = product?.name || 'Este producto';
  const desc = String(product?.description || '').trim();
  const lines = [`🍽️ ${name}`];

  const incItems = getProductIncludedItems(product);
  const incSummary = getProductIncludedSummary(product);
  const explicitBundle = productHasExplicitAssistantBundle(product);

  if (incSummary) {
    lines.push(`✅ Incluye: ${incSummary}.`);
  } else if (desc) {
    lines.push(desc);
  }

  if (
    explicitBundle &&
    wantsSoloOrNoSide(queryNorm) &&
    (incItems.length > 0 || productIncludedHasFries(product) || productIncludedHasDrink(product))
  ) {
    if (variant === 'burger') {
      lines.push(
        `No la manejamos sola: viene con ${incSummary || incItems.join(', ') || 'acompañamiento incluido'}.`
      );
    } else {
      lines.push(
        `Según lo cargado, no va suelto: ${phrase} viene con ${incSummary || incItems.join(', ') || 'lo indicado como incluido en la ficha'}.`
      );
    }
  }

  if (incSummary && desc) {
    lines.push(`Más detalle: ${desc}`);
  }

  const ups = resolvePreferredUpsellSuggestions(product);
  if (ups.length) {
    lines.push(`💡 Podés sumar: ${ups.join(', ')}.`);
  } else {
    for (const bit of resolveNeutralUpsellFallbackLines(product)) {
      lines.push(bit);
    }
  }

  if (variant === 'burger' && productIncludedHasFries(product)) {
    lines.push('🍟 Ya viene con papas incluidas.');
    if (!productIncludedHasDrink(product)) {
      const drinkHint = firstDrinkLikeAddOn(product);
      if (drinkHint) {
        const px = drinkHint.price > 0 ? ` (+$${drinkHint.price.toLocaleString('es-CL')})` : '';
        lines.push(`Si querés bebida, en la ficha figura ${drinkHint.name}${px}.`);
      } else {
        lines.push('Si querés bebida, revisá los agregados de la ficha o la sección de bebidas.');
      }
    }
  }

  const addons = listAddOns(product);
  if (addons?.length) {
    lines.push(`➕ Extras pagados (agregados): ${addons.join('; ')}.`);
    if (productSuggestsExtraProteinAddon(product) && PROTEIN_QUERY_RE.test(queryNorm)) {
      lines.push('Si buscás más proteína o volumen, fijate los extras de la lista 👆');
    }
  } else if (/\b(agregar|añadir|extra|carne|doble)\b/.test(queryNorm)) {
    lines.push(
      'No figuran agregados cargados en la ficha. Podés revisar el producto en el menú o preguntar al local.'
    );
  }

  const rem = listRemovable(product);
  if (rem?.length) {
    lines.push(
      `Ingredientes que podés sacar al armar el pedido (si el local lo permite en el menú): ${rem.join(', ')}.`
    );
  } else if (/\b(sacar|quitar|sin\s|remover|omitir)\b/.test(queryNorm)) {
    lines.push(
      'No figuran ingredientes removibles cargados para este ítem. Igual al pedir en el menú puede haber opciones; si no, consultá por WhatsApp.'
    );
  }

  if (isSideInclusionAsk(queryNorm)) {
    const hasPapa = productIncludedHasFries(product);
    const hasBeb = productIncludedHasDrink(product);
    if (hasPapa && hasBeb) {
      lines.push('Sí: según la ficha, trae acompañamiento tipo papas y bebida incluidos.');
    } else if (hasPapa) {
      lines.push('Sí: según la ficha, trae acompañamiento tipo papas.');
      lines.push('La bebida no figura como incluida en lo cargado.');
    } else if (hasBeb) {
      lines.push('La bebida figura como incluida; el acompañamiento tipo papas no está indicado como incluido.');
    } else {
      lines.push('No figuran papas ni bebida como incluidos en lo cargado.');
    }
  }

  return lines.filter(Boolean).join('\n\n').trim();
}

function answerSingleComesWithBurger(product, n, soloIntent, phrase) {
  const hasFries = productIncludedHasFries(product);
  const hasDrink = productIncludedHasDrink(product);
  const asksPapa = /\b(papa|papas|frita|fritas)\b/.test(n);
  const asksBeb = /\b(bebida|gaseosa|jugo|refresco|coca|sprite|pepsi)\b/.test(n);
  const lines = [];

  if (soloIntent) {
    const inc = getProductIncludedSummary(product);
    const bundled =
      getProductIncludedItems(product).length > 0 ||
      hasFries ||
      hasDrink ||
      productHasExplicitAssistantBundle(product);
    if (bundled) {
      lines.push(
        `No las manejamos solas: ${product.name || phrase} viene con ${inc || 'acompañamiento incluido'}.`
      );
    } else {
      lines.push(`Esta opción la podés pedir sola según lo cargado.`);
    }
    return {
      text: lines.join('\n\n'),
      products: [product],
      fallbackKeys: ['catalog', 'order_hint', 'main'],
    };
  }

  if (asksPapa && asksBeb) {
    if (hasFries && hasDrink) {
      lines.push('Sí: viene con papas y bebida incluidas.');
    } else if (hasFries && !hasDrink) {
      lines.push('Viene con papas.');
      lines.push('La bebida no viene incluida.');
      const daddon = firstDrinkLikeAddOn(product);
      if (daddon) {
        const px = daddon.price > 0 ? ` por $${daddon.price.toLocaleString('es-CL')}` : '';
        lines.push(`Podés agregar ${daddon.name}${px} como extra.`);
      } else {
        lines.push('Podés agregar bebida aparte.');
      }
    } else if (!hasFries && hasDrink) {
      lines.push('No viene con papas incluidas.');
      lines.push('La bebida sí viene incluida.');
    } else {
      lines.push('No viene con papas ni bebida incluidas.');
      lines.push('Podés completar el pedido con los extras que quieras.');
    }
    return {
      text: lines.join('\n\n'),
      products: [product],
      fallbackKeys: ['catalog', 'order_hint', 'main'],
    };
  }

  if (asksPapa) {
    lines.push(hasFries ? 'Sí: viene con papas.' : 'No: no viene con papas incluidas.');
  }
  if (asksBeb) {
    if (hasDrink) {
      lines.push('Sí: la bebida viene incluida.');
    } else {
      lines.push('La bebida no viene incluida.');
      const daddon = firstDrinkLikeAddOn(product);
      if (daddon) {
        const px = daddon.price > 0 ? ` por $${daddon.price.toLocaleString('es-CL')}` : '';
        lines.push(`Podés agregar ${daddon.name}${px}.`);
      } else {
        lines.push('Podés agregarla aparte si el local lo ofrece como extra.');
      }
    }
  }
  if (!lines.length) {
    lines.push(`En ${product.name || phrase} ves el detalle en la lista.`);
  }
  return {
    text: lines.join('\n\n'),
    products: [product],
    fallbackKeys: ['catalog', 'order_hint', 'main'],
  };
}

function answerSingleComesWithDefault(product, n, soloIntent, phrase) {
  const hasFries = productIncludedHasFries(product);
  const hasDrink = productIncludedHasDrink(product);
  const asksPapa = /\b(papa|papas|frita|fritas)\b/.test(n);
  const asksBeb = /\b(bebida|gaseosa|jugo|refresco|coca|sprite|pepsi|cerveza|schop)\b/.test(n);
  const asksSideGeneric =
    /\b(algo|acompañ|acompan|inclu|combo|men[uú])\b/.test(n) &&
    /\b(viene|trae|lleva|inclu|tiene)\b/.test(n);
  const lines = [];

  const inc = getProductIncludedSummary(product);
  const bundled =
    getProductIncludedItems(product).length > 0 ||
    hasFries ||
    hasDrink ||
    productHasExplicitAssistantBundle(product);

  if (soloIntent) {
    if (bundled) {
      lines.push(
        `Según la ficha, ${phrase.toLowerCase()} viene con ${inc || 'ítems incluidos'}. Si querés algo distinto, revisá extras o consultá al local.`
      );
    } else {
      lines.push(`${phrase} podría pedirse sin combo según lo cargado; confirmá en la ficha o con el local.`);
    }
    return {
      text: lines.join('\n\n'),
      products: [product],
      fallbackKeys: ['catalog', 'order_hint', 'main'],
    };
  }

  const incItemsList = getProductIncludedItems(product);
  const incLine = inc || (incItemsList.length ? incItemsList.join(', ') : '');
  if ((asksPapa || asksBeb || asksSideGeneric) && incLine) {
    lines.push(`${phrase} incluye: ${incLine}.`);
    if (asksBeb && !hasDrink) {
      const daddon = firstDrinkLikeAddOn(product);
      if (daddon) {
        const px = daddon.price > 0 ? ` (+$${daddon.price.toLocaleString('es-CL')})` : '';
        lines.push(`Si querés bebida, en extras figura ${daddon.name}${px}.`);
      }
    }
    return {
      text: lines.join('\n\n'),
      products: [product],
      fallbackKeys: ['catalog', 'order_hint', 'main'],
    };
  }

  if (asksPapa || asksBeb) {
    if (asksPapa) {
      lines.push(hasFries ? 'Sí: figura acompañamiento tipo papas en lo incluido.' : 'No: no figuran papas como incluidas.');
    }
    if (asksBeb) {
      lines.push(
        hasDrink
          ? 'Sí: figura bebida en lo incluido.'
          : 'No: la bebida no figura como incluida en lo cargado.'
      );
      if (!hasDrink) {
        const daddon = firstDrinkLikeAddOn(product);
        if (daddon) {
          const px = daddon.price > 0 ? ` por $${daddon.price.toLocaleString('es-CL')}` : '';
          lines.push(`Podés sumar ${daddon.name}${px} como extra.`);
        }
      }
    }
    return {
      text: lines.join('\n\n'),
      products: [product],
      fallbackKeys: ['catalog', 'order_hint', 'main'],
    };
  }

  if (!lines.length && asksSideGeneric && bundled) {
    lines.push(
      `Según la ficha, ${phrase.toLowerCase()} viene armado con ítems o acompañamientos; mirá el detalle en la lista 👇`
    );
  }

  if (!lines.length) {
    lines.push(`En ${product.name || phrase} el detalle está en la ficha 👇`);
  }
  return {
    text: lines.join('\n\n'),
    products: [product],
    fallbackKeys: ['catalog', 'order_hint', 'main'],
  };
}

function answerBurgerComesWithAggregate(burgers, n) {
  const fMaj = majorityIncludedFlag(burgers, productIncludedHasFries);
  const dMaj = majorityIncludedFlag(burgers, productIncludedHasDrink);
  const asksPapa = /\b(papa|papas|frita|fritas)\b/.test(n);
  const asksBeb = /\b(bebida|gaseosa|jugo|refresco|coca|sprite|pepsi)\b/.test(n);
  const parts = [];

  const friesYes = fMaj === 'all' || fMaj === 'majority';
  const drinkYes = dMaj === 'all' || dMaj === 'majority';

  if (asksPapa || (!asksPapa && !asksBeb)) {
    if (fMaj === 'split') {
      parts.push('Varían según la hamburguesa; en la lista ves si traen papas.');
    } else if (friesYes) {
      parts.push('Las hamburguesas vienen con papas.');
    } else {
      parts.push('Las hamburguesas no vienen con papas incluidas.');
    }
  }

  if (asksBeb || (!asksPapa && !asksBeb)) {
    if (dMaj === 'split') {
      parts.push('Con la bebida varía según la opción; revisá la lista.');
    } else if (drinkYes) {
      parts.push('La bebida viene incluida.');
    } else {
      parts.push('La bebida no viene incluida.');
      parts.push('Podés agregarla aparte al pedir.');
    }
  }

  parts.push('Te muestro algunas.');
  return {
    text: parts.join('\n\n'),
    products: burgers.slice(0, 8),
    fallbackKeys: ['catalog', 'order_hint', 'main'],
  };
}

function answerBurgerWantPapasYBebida(burgers) {
  const fMaj = majorityIncludedFlag(burgers, productIncludedHasFries);
  const dMaj = majorityIncludedFlag(burgers, productIncludedHasDrink);
  const friesYes = fMaj === 'all' || fMaj === 'majority';
  const drinkYes = dMaj === 'all' || dMaj === 'majority';
  const drinkNo = dMaj === 'none' || dMaj === 'minority';
  const lines = [];

  if (fMaj === 'split' || dMaj === 'split') {
    lines.push('Depende de la hamburguesa: en la lista ves si ya trae papas y bebida.');
    lines.push('Te muestro opciones.');
    return {
      text: lines.join('\n\n'),
      products: burgers.slice(0, 8),
      fallbackKeys: ['catalog', 'order_hint', 'main'],
    };
  }

  if (friesYes && drinkYes) {
    lines.push('Las hamburguesas vienen con papas y bebida.');
  } else if (friesYes && drinkNo) {
    lines.push('La hamburguesa viene con papas.');
    lines.push('La bebida no viene incluida; podés agregarla aparte.');
  } else if (!friesYes && drinkYes) {
    lines.push('La bebida viene incluida.');
    lines.push('Las papas no vienen incluidas en todas; elegí la opción que prefieras en la lista.');
  } else {
    lines.push('No vienen con papas y bebida incluidas juntas; podés completar el pedido con extras.');
  }

  lines.push('Te muestro algunas.');
  const both = burgers.filter((p) => productIncludedHasFries(p) && productIncludedHasDrink(p));
  const picks = friesYes && drinkYes && both.length ? both : burgers;
  return {
    text: lines.join('\n\n'),
    products: picks.slice(0, 8),
    fallbackKeys: ['catalog', 'order_hint', 'main'],
  };
}

export const defaultStrategy = {
  describeComposition(ctx) {
    return buildDescribeCompositionLines(ctx, 'default');
  },
  answerSingleComesWith(product, n, soloIntent, menuData, business) {
    const phrase = resolveReadableProductPhrase(product, business, menuData);
    return answerSingleComesWithDefault(product, n, soloIntent, phrase);
  },
};

export const burgerStrategy = {
  describeComposition(ctx) {
    return buildDescribeCompositionLines(ctx, 'burger');
  },
  answerSingleComesWith(product, n, soloIntent, menuData, business) {
    const phrase = resolveReadableProductPhrase(product, business, menuData);
    return answerSingleComesWithBurger(product, n, soloIntent, phrase);
  },
  answerBurgerComesWithAggregate,
  answerBurgerWantPapasYBebida,
};

/**
 * @param {object} product
 * @param {object} business
 * @param {object} menuData
 */
export function pickAssistantCompositionStrategy(product, business, menuData) {
  if (!product) return defaultStrategy;
  if (resolveReadableProductFamily(product, business, menuData) === 'hamburguesa' || productIsBurgerish(product)) {
    return burgerStrategy;
  }
  return defaultStrategy;
}
